import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useTableQuery } from '@/hooks/useTableQuery';
import { paymentsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { Payment, PaymentMethod, PaymentStatus } from '@/types/finance';
import { formatDate, orDash } from '@/utils/format';
import { Money, PaymentStatusBadge } from './shared';
import { RecordPaymentDialog } from './RecordPaymentDialog';

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_WALLET', label: 'Mobile wallet' },
  { value: 'OTHER', label: 'Other' },
];

const STATUSES: { value: PaymentStatus; label: string }[] = [
  { value: 'PENDING', label: 'Not cleared' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIALLY_REFUNDED', label: 'Partly refunded' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'CANCELLED', label: 'Voided' },
];

/** Money received, and what it settled. */
export function PaymentsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const table = useTableQuery('paymentDate,desc');

  const [statusFilter, setStatusFilter] = useState<PaymentStatus | ''>('');
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | ''>('');
  const [unallocatedOnly, setUnallocatedOnly] = useState(false);
  const [recording, setRecording] = useState(false);

  const params = {
    ...table.params,
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
    ...(methodFilter !== '' ? { method: methodFilter } : {}),
    ...(unallocatedOnly ? { unallocatedOnly: true } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.payments.list(params),
    queryFn: () => paymentsApi.list(params),
  });

  const columns: Column<Payment>[] = [
    {
      key: 'receipt',
      header: 'Receipt',
      sortKey: 'receiptNumber',
      render: (payment) => (
        <div>
          <span className="font-mono text-xs font-medium text-ink">{payment.receiptNumber}</span>
          <span className="block text-[11px] text-subtle">
            {payment.paymentMethod.replaceAll('_', ' ').toLowerCase()}
            {payment.referenceNumber !== undefined ? ` · ${payment.referenceNumber}` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (payment) => (
        <div>
          <span className="text-ink">{orDash(payment.studentName)}</span>
          {payment.payerName !== undefined && (
            <span className="block text-[11px] text-muted">paid by {payment.payerName}</span>
          )}
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      sortKey: 'paymentDate',
      render: (payment) => (
        <span className="whitespace-nowrap tabular-nums">{formatDate(payment.paymentDate)}</span>
      ),
    },
    {
      key: 'amount',
      header: 'Received',
      sortKey: 'amount',
      className: 'text-right',
      render: (payment) => <Money amount={payment.amount} bold />,
    },
    {
      key: 'allocated',
      header: 'Applied',
      className: 'text-right',
      render: (payment) => <Money amount={payment.allocatedAmount} />,
    },
    {
      key: 'held',
      header: 'Held',
      className: 'text-right',
      render: (payment) => <Money amount={payment.unallocatedAmount} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (payment) => <PaymentStatusBadge status={payment.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Payments"
        description="Every payment taken. “Held” is money received that has not yet been applied to a bill."
        icon={<Banknote className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.PAYMENT_RECORD) ? (
            <Button onClick={() => setRecording(true)}>Record payment</Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by receipt or reference…',
        }}
        filters={
          <>
            <FilterSelect label="Status" value={statusFilter}
                          onChange={(value) => { setStatusFilter(value); table.setPage(0); }}
                          options={STATUSES} />
            <FilterSelect label="Method" value={methodFilter}
                          onChange={(value) => { setMethodFilter(value); table.setPage(0); }}
                          options={METHODS} />
            <label className="flex items-center gap-2 text-sm text-muted">
              <input type="checkbox" checked={unallocatedOnly}
                     onChange={(event) => { setUnallocatedOnly(event.target.checked); table.setPage(0); }}
                     className="h-4 w-4 border-line" />
              Money held on account
            </label>
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(payment) => payment.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(payment) => navigate(ROUTES.paymentDetail(payment.id))}
          emptyTitle="No payments yet"
          emptyDescription="Record a payment to settle a family's outstanding bills."
          emptyIcon={<Banknote className="h-6 w-6" aria-hidden="true" />}
          emptyAction={
            can(PERMISSIONS.PAYMENT_RECORD) ? (
              <Button onClick={() => setRecording(true)}>Record payment</Button>
            ) : undefined
          }
        />
      </ListPage>

      <RecordPaymentDialog open={recording} onClose={() => setRecording(false)} />
    </>
  );
}
